import { Form, Input, Modal, notification } from 'antd';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import APIClient from '../api/api';
import { useTheme } from '../theme';
import { useAuth } from '../auth/AuthContext';
import { useDomainModal } from '../components/DomainModalContext';
import { findActiveNav } from './Sidebar.nav';

export function TopBar() {
  const location = useLocation();
  const { mode, toggle } = useTheme();
  const { user, logout } = useAuth();
  const { open: openDomainModal } = useDomainModal();
  const active = findActiveNav(location.pathname);
  const [search, setSearch] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [changePasswordForm] = Form.useForm();
  const menuRef = useRef<HTMLDivElement>(null);

  async function handleChangePassword(values: { currentPassword: string; newPassword: string }) {
    try {
      setChangingPassword(true);
      await APIClient.changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      notification.success({ message: 'Password changed.' });
      setChangePasswordOpen(false);
      changePasswordForm.resetFields();
    } catch (e: any) {
      notification.error({
        message: e?.response?.data?.error || 'Failed to change password.',
      });
    } finally {
      setChangingPassword(false);
    }
  }

  useEffect(() => {
    if (!menuOpen) return;
    function onClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen]);

  function onSearch(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const q = search.trim();
    if (!q) return;
    openDomainModal(q);
    setSearch('');
  }

  const sectionLabel = active.section?.title;
  const parentLabel = active.parent?.label;
  const itemLabel = active.item?.label ?? 'Dashboard';

  return (
    <header className="h-[52px] border-b border-border bg-panel flex items-center px-5 gap-4 flex-shrink-0">
      {/* Breadcrumb */}
      <div className="text-[13px] text-text-3 flex items-center gap-1.5 min-w-0">
        {sectionLabel && (
          <>
            <span className="truncate">{sectionLabel}</span>
            <span>/</span>
          </>
        )}
        {parentLabel && (
          <>
            <span className="truncate">{parentLabel}</span>
            <span>/</span>
          </>
        )}
        <span className="text-text font-medium truncate">{itemLabel}</span>
      </div>

      <div className="ml-auto flex items-center gap-2.5">
        <form onSubmit={onSearch}>
          <div className="bg-bg-elevated border border-transparent rounded-md px-3 py-1.5 w-[260px] flex items-center gap-2 focus-within:border-border-strong">
            <span className="text-text-3 text-[13px]">⌕</span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search domain…"
              className="bg-transparent border-none outline-none flex-1 text-[12.5px] text-text placeholder:text-text-3 min-w-0"
            />
          </div>
        </form>

        <button
          type="button"
          onClick={toggle}
          aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          className="w-[30px] h-[30px] rounded-md border border-border bg-panel flex items-center justify-center cursor-pointer text-text-2 hover:bg-bg-elevated hover:text-text"
        >
          {mode === 'dark' ? '☀' : '◐'}
        </button>

        {user && (
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              className="h-[30px] px-2.5 rounded-md border border-border bg-panel flex items-center gap-2 cursor-pointer text-text-2 hover:bg-bg-elevated hover:text-text text-[12.5px]"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            >
              <span className="w-[20px] h-[20px] rounded-full bg-bg-elevated flex items-center justify-center text-[11px] font-semibold text-text">
                {(user.name || user.email).charAt(0).toUpperCase()}
              </span>
              <span className="truncate max-w-[140px]">{user.name || user.email}</span>
            </button>
            {menuOpen && (
              <div
                role="menu"
                className="absolute right-0 mt-1 w-[220px] bg-panel border border-border rounded-md shadow-lg overflow-hidden z-50"
              >
                <div className="px-3 py-2 border-b border-border">
                  <div className="text-[12.5px] font-medium text-text truncate">{user.name || user.email}</div>
                  <div className="text-[11px] text-text-3 truncate">{user.email}</div>
                  <div className="text-[10px] uppercase tracking-wider text-text-3 mt-0.5">{user.role}</div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    setChangePasswordOpen(true);
                  }}
                  className="w-full text-left px-3 py-2 text-[12.5px] text-text-2 hover:bg-bg-elevated hover:text-text cursor-pointer"
                >
                  Change password
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    logout();
                  }}
                  className="w-full text-left px-3 py-2 text-[12.5px] text-text-2 hover:bg-bg-elevated hover:text-text cursor-pointer border-t border-border"
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        title="Change password"
        open={changePasswordOpen}
        onCancel={() => {
          if (changingPassword) return;
          setChangePasswordOpen(false);
          changePasswordForm.resetFields();
        }}
        onOk={() => changePasswordForm.submit()}
        okText="Change password"
        confirmLoading={changingPassword}
        maskClosable={!changingPassword}
      >
        <Form form={changePasswordForm} onFinish={handleChangePassword} layout="vertical">
          <Form.Item
            label="Current password"
            name="currentPassword"
            rules={[{ required: true, message: 'Please enter your current password' }]}
          >
            <Input.Password autoComplete="current-password" />
          </Form.Item>
          <Form.Item
            label="New password"
            name="newPassword"
            rules={[
              { required: true, message: 'Please enter a new password' },
              { min: 10, message: 'Password must be at least 10 characters' },
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            label="Confirm new password"
            name="confirmPassword"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Please confirm your new password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>
    </header>
  );
}
